import { Button, ButtonProps, Menu, MenuItem } from '@mui/material'
import React from 'react'

import Icon from 'components/Icon'
import { useAnchorElement } from 'hooks/use-anchor-element'
import useAuth from 'hooks/use-auth'
import { useTranslation } from 'hooks/use-translation'
import { FAILURE_FLAGS_STALE_TIME } from 'queries/failure-flags/query-utils'
import { useGetFailureFlags } from 'queries/failure-flags/use-get-failure-flags'
import { useGetFailureFlagsApps } from 'queries/failure-flags/use-get-failure-flags-apps'
import { useGetFailureFlagsExperiments } from 'queries/failure-flags/use-get-failure-flags-experiments'

export const AddNodeMenu: React.FC<
  {
    id: string
    label?: string
    onAddAttackNode: () => void
    onAddCompletedAttack: () => void
    onAddConcurrentNode?: () => void
    onAddDelayNode: () => void
    onAddExistingFailureFlagExperiment: () => void
    onAddFailureFlagNode: () => void
    onAddStatusCheckNode: () => void
  } & ButtonProps
> = ({
  id,
  label,
  onAddAttackNode,
  onAddCompletedAttack,
  onAddConcurrentNode,
  onAddDelayNode,
  onAddExistingFailureFlagExperiment,
  onAddFailureFlagNode,
  onAddStatusCheckNode,
  ...rest
}) => {
  const { hasFailureFlags: hasFailureFlagsEntitlements } = useAuth()
  const { trans } = useTranslation()

  const {
    anchorElement: anchorEl,
    handleAnchorElementClick: handleOpenMenu,
    handleAnchorElementClose: handleCloseMenu,
    isOpen: isMenuOpen
  } = useAnchorElement()

  const { data: failureFlags, isSuccess: hasLoadedFailureFlags } =
    useGetFailureFlags({
      staleTime: FAILURE_FLAGS_STALE_TIME
    })

  const hasFlags = Boolean(hasLoadedFailureFlags && failureFlags?.length)

  const { data: applications, isSuccess: hasLoadedApplications } =
    useGetFailureFlagsApps({
      staleTime: FAILURE_FLAGS_STALE_TIME
    })

  const hasApplications = Boolean(hasLoadedApplications && applications?.length)

  const { data: experiments, isSuccess: hasLoadedExperiments } =
    useGetFailureFlagsExperiments({
      staleTime: FAILURE_FLAGS_STALE_TIME
    })

  const hasExperiments = Boolean(hasLoadedExperiments && experiments?.length)

  // It's okay to fail silently for now
  const isNewFailureFlagImpactOptionVisible =
    hasFailureFlagsEntitlements && hasFlags && hasApplications

  const isExistingFailureFlagExperimentOptionVisible =
    isNewFailureFlagImpactOptionVisible && hasExperiments

  const buttonId = `add-node-menu-${id}`
  const menuId = `add-node-menu-${id}`

  const menuItems = [
    {
      name: 'New Experiment',
      disabled: false,
      onClick: () => {
        handleCloseMenu()
        onAddAttackNode()
      },
      testId: 'add-new-attack-node-menu-item',
      isVisible: true
    },
    {
      name: 'Completed Experiment',
      disabled: false,
      onClick: () => {
        handleCloseMenu()
        onAddCompletedAttack()
      },
      testId: 'add-completed-attack-node-menu-item',
      isVisible: true
    },
    {
      name: 'New Failure Flag Impact',
      disabled: false,
      onClick: () => {
        handleCloseMenu()
        onAddFailureFlagNode()
      },
      testId: 'add-failure-flag-node-menu-item', // TODO
      isVisible: isNewFailureFlagImpactOptionVisible
    },
    {
      name: 'Existing Failure Flag Experiment',
      disabled: false,
      onClick: () => {
        handleCloseMenu()
        onAddExistingFailureFlagExperiment()
      },
      testId: 'add-existing-failure-flag-node-menu-item', // TODO
      isVisible: isExistingFailureFlagExperimentOptionVisible
    },
    {
      name: trans('status_check'),
      disabled: false,
      onClick: () => {
        handleCloseMenu()
        onAddStatusCheckNode()
      },
      testId: 'add-status-check-node-menu-item',
      isVisible: true
    },
    {
      name: 'Delay',
      disabled: false,
      onClick: () => {
        handleCloseMenu()
        onAddDelayNode()
      },
      testId: 'add-delay-node-menu-item',
      isVisible: true
    },
    {
      name: 'Concurrent Node',
      disabled: !onAddConcurrentNode, // TODO display tooltip on why this is disabled https://gremlininc.atlassian.net/browse/EN-8009
      onClick: onAddConcurrentNode
        ? () => {
            handleCloseMenu()
            onAddConcurrentNode()
          }
        : undefined,
      testId: 'add-concurrent-node-menu-item',
      isVisible: true
    }
  ] as const

  return (
    <div>
      <Button
        aria-controls={isMenuOpen ? menuId : undefined}
        aria-expanded={isMenuOpen ? 'true' : undefined}
        aria-haspopup="true"
        aria-label={trans('scenarios.add_to_scenario')}
        data-testid="add-node-menu-button"
        endIcon={<Icon icon="solidCaretDown" />}
        id={buttonId}
        size="small"
        variant="outlined"
        onClick={handleOpenMenu}
        {...rest}
      >
        {label ?? trans('add')}
      </Button>
      <Menu
        anchorEl={anchorEl}
        id={menuId}
        MenuListProps={{
          'aria-labelledby': buttonId
        }}
        open={isMenuOpen}
        onClose={handleCloseMenu}
      >
        {menuItems
          .filter((item) => item.isVisible)
          .map(({ disabled, name, onClick, testId }) => (
            <MenuItem
              key={testId}
              data-testid={testId}
              disabled={disabled}
              onClick={onClick}
            >
              {name}
            </MenuItem>
          ))}
      </Menu>
    </div>
  )
}
